import {
  Button,
  EButtonColorVariant,
  EIconButtonVariant,
  ETextStyleVariant,
  GeneralBulbIcon,
  GeneralCloseIcon,
  IconButton,
  Overlay,
  Text,
} from '@outdoorsyco/bonfire';
import cn from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMenuTriggerState } from 'react-stately';

import { useGuidedSearchContext } from '@/components/guided-search/GuidedSearchContext';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import useQueryParams from '@/hooks/useQueryParams';
import { getCookie, setCookie } from '@/utility/cookie';

import DropdownButton from '../../DropdownButton/DropdownButton';
import css from './HelpMeSearchButton.module.css';

const PROMO_COOKIE_NAME = 'guided-search-promo-shown';
const COOKIE_EXPIRATION_DATE = 400; // in days, upper limit of chrome cookie expiration

interface IProps {
  onClick: () => void;
  icon?: React.ReactNode;
}

const HelpMeSearchButton: React.FC<IProps> = ({ onClick, icon }) => {
  const { isMobile } = useBreakpoint();
  const { reset: resetGuidedSearch, hasSelectedData } = useGuidedSearchContext();
  const [showNotification, setShowNotification] = useState<null | boolean>(null);
  const intl = useIntl();

  const overlayRef = useRef<HTMLButtonElement>(null);
  const queryParams = useQueryParams();
  const overlayTriggerState = useMenuTriggerState({
    isOpen: Boolean(showNotification),
    onOpenChange: () => {
      setShowNotification(false);
    },
  });

  useEffect(() => {
    if (isMobile && showNotification === null) {
      const promoCookie = getCookie(PROMO_COOKIE_NAME);

      if (!promoCookie || promoCookie !== 'true') {
        setShowNotification(true);
        setCookie(PROMO_COOKIE_NAME, 'true', { expires: COOKIE_EXPIRATION_DATE });

        setTimeout(() => {
          setShowNotification(false);
        }, 5000);
      } else {
        setShowNotification(false);
      }
    }
  }, [isMobile, showNotification]);

  const isDelivery = queryParams.delivery === 'true';
  const isDeliveryStationary = queryParams.deliveryStationary === 'stationary';
  useEffect(() => {
    if (isMobile && isDelivery && isDeliveryStationary) {
      setShowNotification(false);
    }
  }, [isMobile, isDelivery, isDeliveryStationary]);

  const handleClick = () => {
    if (showNotification) {
      setShowNotification(false);
    }
    resetGuidedSearch();
    onClick?.();
  };

  const handleCloseNotification = () => {
    setShowNotification(false);
  };

  if (isMobile) {
    return (
      <div>
        <Button
          className={cn(
            // Icon-only on mWeb: tighter horizontal padding (!px-3 overrides the
            // Bonfire Button's default) and justify-center so the icon sits centered.
            'relative flex items-center justify-center !px-3 border rounded-full border-gray-310 whitespace-nowrap h-[2.125rem] md:h-10',
            {
              '!bg-gray-800 !border-gray-800': hasSelectedData,
            },
          )}
          ref={overlayRef}
          label={icon ?? <GeneralBulbIcon className={cn({ 'text-white': hasSelectedData })} />}
          aria-label={intl.formatMessage({ defaultMessage: 'Help me search', id: '6tZiBh' })}
          variant={EButtonColorVariant.Secondary}
          onClick={handleClick}
        />
        {showNotification && (
          <Overlay triggerRef={overlayRef} triggerState={overlayTriggerState}>
            <div className="relative p-6 mx-4 text-white bg-gray-800 top-3 w-fit z-60 rounded-3xl max-w-[420px]">
              <div className={css.arrow} />
              <IconButton
                className="absolute rounded-full top-[8px] right-[8px] !bg-gray-50"
                icon={GeneralCloseIcon}
                size="small"
                variant={EIconButtonVariant.Transparent}
                onClick={handleCloseNotification}
              />
              <Text className="py-2" variant={ETextStyleVariant.MediumRegular}>
                <FormattedMessage
                  defaultMessage="Narrow results to find your perfect ride. Tap here."
                  id="sAG5A5"
                />
              </Text>
            </div>
          </Overlay>
        )}
      </div>
    );
  }

  return (
    <div className="items-center hidden h-10 md:flex md:h-11 p-0.5">
      <DropdownButton
        onClick={() => {
          resetGuidedSearch();
          onClick?.();
        }}
        customIcon={<></>}
        title={
          <>
            {icon ?? <GeneralBulbIcon className="mr-1 -ml-1 text-lg text-gray-900" />}
            {intl.formatMessage({ defaultMessage: 'Help me search', id: '6tZiBh' })}
          </>
        }
      />
    </div>
  );
};

export default HelpMeSearchButton;
